import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { Bold, Italic } from "lucide-react";
import { Toggle } from "@/components/ui/toggle";
import { useEffect } from "react";
import { sanitizeExcerptHtml as sanitize } from "./richTextEditorUtils";

type Props = {
  value: string;
  onChange: (html: string, text: string) => void;
  placeholder?: string;
  className?: string;
};

export const RichTextEditor = ({ value, onChange, placeholder, className }: Props) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false, bulletList: false, orderedList: false, blockquote: false,
        code: false, codeBlock: false, horizontalRule: false, strike: false,
      }),
      Placeholder.configure({ placeholder: placeholder ?? "Paste or type the excerpt…" }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      const html = sanitize(editor.getHTML());
      onChange(html, editor.getText());
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div className={`excerpt-editor border border-input rounded-md bg-background ${className ?? ""}`}>
      <div className="flex gap-1 border-b border-border p-1">
        <Toggle size="sm" pressed={editor.isActive("bold")} onPressedChange={() => editor.chain().focus().toggleBold().run()} aria-label="Bold">
          <Bold className="h-4 w-4" />
        </Toggle>
        <Toggle size="sm" pressed={editor.isActive("italic")} onPressedChange={() => editor.chain().focus().toggleItalic().run()} aria-label="Italic">
          <Italic className="h-4 w-4" />
        </Toggle>
      </div>
      <EditorContent editor={editor} className="px-3 py-2 text-base leading-relaxed" />
    </div>
  );
};
